import React, { useState, useEffect } from 'react';
import API from '../services/api';
import {
  getTransactions, TransactionListResult,
  getConfig,
} from '../services/adminService';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import {
  pageContainer, cardStyle, statsGrid, labelStyle, valueStyle, subTextStyle,
  sectionTitle, thStyle, tdStyle, badgeStyle, tableSearch, smallSelect,
} from '../styles/theme';
import { useAdminPage, useDebounce, useToast } from '../utils/useAdminPage';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';
import { useIsMobile } from '../utils/useIsMobile';
import { Link } from 'react-router-dom';
import { exportToCSV } from '../utils/csvExport';
import ChartFilterBar, { ChartPeriod } from '../components/Charts/ChartFilterBar';

// ── Types ────────────────────────────────────────────────────────────────────
const EMPTY: TransactionListResult = {
  items: [],
  summary: { total: 0, successful: 0, pending: 0, failed: 0, totalRevenue: 0 },
};

// ── Main Finance Page ────────────────────────────────────────────────────────
const Finance = () => {
  const isMobile = useIsMobile();
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [page, setPage]           = useState(1);
  const [commissionRate, setCommissionRate] = useState(0.15);
  const [finRevPeriod, setFinRevPeriod] = useState<ChartPeriod>('month');
  const [revChartData, setRevChartData] = useState<any[]>([]);
  const [revChartLoading, setRevChartLoading] = useState(false);

  const pageSize = 10;
  const dSearch  = useDebounce(search, 350);
  const { toast } = useToast();

  // Cache key includes both search and status filter
  const cacheKey = `${dSearch}|${statusFilter}`;

  const { data, loading, revalidating, syncLabel, lastSyncedAt, refresh } =
    useAdminPage<TransactionListResult>(EMPTY, {
      cacheNs: 'finance:',
      cacheKeySuffix: cacheKey,
      // Pass statusFilter as the 'type' param — backend maps it to status field
      fetcher: () => getTransactions({ search: dSearch, type: statusFilter }),
    });

  // Fetch config on mount
  useEffect(() => {
    getConfig('FINANCE').then((configs) => {
      if (configs?.length) {
        const c = configs.find((c: any) => c.key === 'COMMISSION_RATE');
        if (c) setCommissionRate(parseFloat(c.value));
      }
    });
  }, []);

  // Fetch period-aware revenue chart from backend
  useEffect(() => {
    let cancelled = false;
    const fetchChart = async () => {
      setRevChartLoading(true);
      try {
        const res = await API.get(`/admin/revenue-chart?period=${finRevPeriod}`);
        if (!cancelled && res.data?.success) setRevChartData(res.data.data || []);
      } catch { /* silent */ } finally {
        if (!cancelled) setRevChartLoading(false);
      }
    };
    fetchChart();
    return () => { cancelled = true; };
  }, [finRevPeriod]);

  const items   = React.useMemo(() => data.items || [], [data.items]);
  const summary = data.summary;
  const paged   = items.slice((page - 1) * pageSize, page * pageSize);

  const stats = [
    {
      label: 'Total Revenue', icon: '₹', color: '#2563eb', highlight: true,
      val: `₹${(summary.totalRevenue || 0).toLocaleString()}`,
      sub: `${summary.successful || 0} successful payments`,
    },
    {
      label: 'Platform Commission', icon: '%', color: '#10b981',
      val: `₹${((summary.totalRevenue || 0) * commissionRate).toLocaleString()}`,
      sub: `${(commissionRate * 100).toFixed(1)}% commission rate`,
    },
    {
      label: 'Pending Transactions', icon: '⏳', color: '#f59e0b',
      val: (summary.pending || 0).toLocaleString(),
      sub: 'Awaiting confirmation',
    },
    {
      label: 'All Transactions', icon: '📊', color: '#8b5cf6',
      val: (summary.total || 0).toLocaleString(),
      sub: `${summary.failed || 0} failed`,
    },
  ];


  // ── Badge colours ──────────────────────────────────────────────────────────
  const statusBadge = (s: string) => {
    if (s === 'SUCCESS')  return { bg: '#f0fdf4', color: '#15803d', label: '● Success' };
    if (s === 'PENDING')  return { bg: '#fffbeb', color: '#b45309', label: '● Pending' };
    if (s === 'FAILED')   return { bg: '#fef2f2', color: '#b91c1c', label: '● Failed'  };
    if (s === 'REFUNDED') return { bg: '#f5f3ff', color: '#7c3aed', label: '● Refunded' };
    return { bg: '#f1f5f9', color: 'hsl(var(--muted-foreground))', label: s };
  };

  return (
    <div style={pageContainer}>
      {/* Global Animations */}
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        @keyframes spin    { to { transform: rotate(360deg) } }
      `}</style>

      <Toast toast={toast} />


      {/* Page Header */}
      <PageHeader
        breadcrumb="Dashboard > Finance"
        title="Finance & Payouts"
        subtitle="Manage transactions, scholar payouts, and platform revenue."
        syncLabel={syncLabel}
        revalidating={revalidating}
        loading={loading}
        lastSyncedAt={lastSyncedAt}
        onRefresh={refresh}
        onExport={() => {
          const exportData = items.map((t: any) => ({
            TransactionID: t.id,
            User: t.booking?.customer?.user?.name || 'N/A',
            Service: t.booking?.service?.name || 'N/A',
            Amount: t.amount || 0,
            Commission: (t.amount || 0) * commissionRate,
            Status: t.status,
            Date: t.createdAt ? new Date(t.createdAt).toLocaleDateString() : 'N/A',
          }));
          exportToCSV(exportData, 'Finance_Transactions_Report');
        }}
      />

      {/* ── Stats Grid ── */}
      <div style={statsGrid}>
        {stats.map((s, i) => (
          <div key={i} style={{
            ...cardStyle,
            ...(s.highlight ? {
              background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
              color: '#fff',
            } : {}),
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ ...labelStyle, ...(s.highlight ? { color: 'rgba(255,255,255,0.85)' } : {}) }}>
                {s.label}
              </span>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
            </div>
            <div style={{ ...valueStyle, ...(s.highlight ? { color: '#fff', fontSize: 26 } : {}) }}>
              {s.val}
            </div>
            <div style={{ ...subTextStyle, ...(s.highlight ? { color: 'rgba(255,255,255,0.7)' } : {}) }}>
              {s.sub}
            </div>
          </div>
        ))}
      </div>

      {/* ── Revenue Chart + Pending Payouts ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: 24, marginBottom: 28 }}>

        {/* Revenue Chart */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={sectionTitle}>Revenue Overview</h3>
            <ChartFilterBar value={finRevPeriod} onChange={setFinRevPeriod} />
          </div>
          {revChartLoading ? (
            <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'spin 0.7s linear infinite' }} />
            </div>
          ) : revChartData.length === 0 ? (
            <div style={{ height: 250, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: 28, marginBottom: 12, color: 'hsl(var(--muted-foreground))' }}>No data</div>
              <div style={{ fontSize: 14 }}>No revenue for this period.</div>
            </div>
          ) : (
            <div style={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `₹${v}`} />
                  <RechartsTooltip cursor={{ fill: '#f1f5f9' }} formatter={(v: any) => `₹${Number(v).toFixed(2)}`} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue"    name="Revenue"    fill="#3b82f6" radius={[4,4,0,0]} maxBarSize={40} />
                  <Bar dataKey="commission" name="Commission" fill="#10b981" radius={[4,4,0,0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Payouts & Refunds Navigation Card */}
        <div style={{
          ...cardStyle,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
          color: '#fff',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 28, marginBottom: 12 }}>💸</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800 }}>Payouts & Refunds</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
              Process pending scholar payouts, view all processed payouts, and manage customer refunds.
            </p>
          </div>
          <Link
            to="/finance/payouts"
            style={{
              display: 'inline-block', padding: '11px 20px',
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              border: '1.5px solid rgba(255,255,255,0.4)',
              borderRadius: 12, fontSize: 13, fontWeight: 700,
              textDecoration: 'none', textAlign: 'center',
              backdropFilter: 'blur(4px)', transition: 'all 0.15s',
            }}
          >
            Manage Payouts &amp; Refunds →
          </Link>
        </div>
      </div>

      {/* ── Transaction Table ── */}
      <div style={cardStyle}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <h3 style={{ ...sectionTitle, marginBottom: 2 }}>All Transactions</h3>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
              {loading ? 'Loading…' : `${items.length} record${items.length !== 1 ? 's' : ''} found`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by ID or user name…"
              style={{ ...tableSearch, width: 240 }}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
            <select
              style={smallSelect}
              value={statusFilter}
              onChange={e => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="">All Status</option>
              <option value="SUCCESS">Successful</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '2px solid #f1f5f9' }}>
              <th style={thStyle}>TRANSACTION ID</th>
              <th style={thStyle}>USER</th>
              <th style={thStyle}>SERVICE</th>
              <th style={thStyle}>AMOUNT</th>
              <th style={thStyle}>COMMISSION</th>
              <th style={thStyle}>STATUS</th>
              <th style={thStyle}>DATE</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: 60, textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8', fontSize: 14 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      border: '3px solid #e2e8f0', borderTopColor: '#3b82f6',
                      animation: 'spin 0.7s linear infinite',
                      margin: '0 auto 12px',
                    }} />
                    Loading transactions…
                  </div>
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 60, textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8' }}>
                    <div style={{ fontSize: 22, marginBottom: 12 }}>No payouts</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>No transactions found</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      {search || statusFilter
                        ? 'Try adjusting your search or status filter.'
                        : 'Transactions will appear here once payments are made.'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : paged.map((t: any) => {
              const badge = statusBadge(t.status);
              const commission = (t.amount || 0) * commissionRate;
              return (
                <tr
                  key={t.id}
                  style={{
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'monospace', color: '#334155' }}>
                      #{t.id?.slice(0, 10).toUpperCase() || '—'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'hsl(var(--foreground))' }}>
                      {t.booking?.customer?.user?.name || '—'}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {t.booking?.service?.name ? (
                      <span style={badgeStyle}>{t.booking.service.name}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: 'hsl(var(--foreground))' }}>
                      ₹{(t.amount || 0).toLocaleString()}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>
                      ₹{commission.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 11,
                      fontWeight: 700, background: badge.bg, color: badge.color,
                    }}>
                      {badge.label}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      }) : '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Paginator total={items.length} page={page} pageSize={pageSize} onPage={setPage} />
      </div>
    </div>
  );
};

export default Finance;
